import { ModuleWithProviders, NgModule } from '@angular/core';
import { NavHeaderComponent } from './components/nav-header/nav-header.component';
import { CommonModule, DecimalPipe } from '@angular/common';
import { AuthCallbackComponent } from './components/auth-callback/auth-callback.component';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbDateParserFormatter, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LoggedOutComponent } from './components/logged-out/logged-out.component';
import { PageNotFoundComponent } from './components/page-not-found/page-not-found.component';
import { ClientNavWrapperComponent } from './components/client-nav-wrapper/client-nav-wrapper.component';
import { NgIdleKeepaliveModule } from '@ng-idle/keepalive';
import { AuthIdleComponent } from './components/auth-idle/auth-idle.component';
import { LoadingScreenComponent } from './components/loading-screen/loading-screen.component';
import { SearchComponent } from './components/search/search.component';
import { ClientHomeComponent } from './components/client-home/client-home.component';
// import { GtagModule } from 'angular-gtag';
import { ClientSubNavComponent } from './components/client-sub-nav/client-sub-nav.component';
import { ClientNotesModalComponent } from './components/client-notes-modal/client-notes-modal.component';
import { ConfirmModalComponent } from './components/confirm-modal/confirm-modal.component';
import { HttpErrorInterceptor } from './interceptors/http-error-interceptor';
import { LoadingScreenInterceptor } from './interceptors/loading-interceptor';
import { InstanceTypeInterceptor } from './interceptors/instance-type-interceptor';
import { PassThruWrapperComponent } from './components/pass-thru-wrapper/pass-thru-wrapper.component';
import { ReportSelectorComponent } from './components/report-selector/report-selector.component';
import { QuarterPickerComponent } from './components/quarter-picker/quarter-picker.component';
import { QuickReportsComponent } from './components/client-home/quick-reports/quick-reports.component';
import { DateSuffix } from './pipes/date-suffix.pipe';
import { CPGTagEventDirective } from './directives/cpGTag-event.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { ReleaseNotesComponent } from './components/release-notes/release-notes.component';
import { ArticleComponent } from './components/article/article.component';
import { ArticleFullPageComponent } from './components/article-full-page/article-full-page.component';
import { CamelCaseToTitleCasePipe } from './pipes/camel-case-to-title-case.pipe';
import { EnumToKeyValuePipe } from './pipes/enum-to-keyvalue.pipe';
import { AutoCompleteInputComponent } from './components/client-autocomplete-input/client-autocomplete-input.component';
import { AlertModalComponent } from './components/alert-modal/alert-modal.component';
import { FlyInModalComponent } from './components/fly-in-modal/fly-in-modal.component';
import { MissingPermissionsComponent } from './components/missing-permissions/missing-permissions.component';
import { HasMinRoleDirective } from './directives/has-min-role.directive';
import { ModalService } from './services/modal.service';
import { CanDeactivatePersonGuard } from './guards/can-deactivate-person.guard';
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from 'ngx-mask';
import { NumberInputDirective } from './directives/number-input.directive';
import { ConfettiEffectComponent } from './components/confetti-effect/confetti-effect.component';
import { CellRendererComponent } from './components/grid/cell-renderer/cell-renderer.component';
import { CellEditorComponent } from './components/grid/cell-editor/cell-editor.component';
import { CustomHeaderIconComponent } from './components/grid/custom-header-icon/custom-header-icon.component';
import { GridCheckboxComponent } from './components/grid/grid-checkbox/grid-checkbox.component';
import { MorePayrollActionsPopup } from './components/grid/more-payroll-actions-popup/more-payroll-actions-popup';
import { CustomGridIconComponent } from './components/grid/custom-grid-icon/custom-grid-icon.component';
import { CustomTooltipComponent } from './components/grid/custom-tooltip/custom-tooltip.component';
import { BaseGridInputComponent } from './components/grid/base-grid-input/base-grid-input.component';
import { CanDeactivatePayrollGuard } from './guards/can-deactivate-payroll.guard';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { PayrollWipCSRComponent } from './components/external-reports/payroll-wip-csr/payroll-wip-csr.component';
import { PayrollWipSupervisorComponent } from './components/external-reports/payroll-wip-supervisor/payroll-wip-supervisor.component';
import { ThirdPartyEmbedComponent } from './components/third-party-embed/third-party-embed.component';
import { MonthPickerComponent } from './components/month-picker/month-picker.component';
import { GenericPickerComponent } from './components/generic-picker/generic-picker.component';
import { NgbDateCustomParserFormatter } from './utils/datepicker-parser-formatter';
import { CachingInterceptor } from './interceptors/caching-interceptor';
import { CheckboxComponent } from './components/checkbox/checkbox.component';
import { MultiselectComponent } from './components/multiselect/multiselect.component';
import { DisabledFieldsetComponent } from './components/disabled-fieldset/disabled-fieldset.component';
import { UnauthenticatedLandingComponent } from './components/unauthenticated-landing/unauthenticated-landing.component';
import { DateRangePickerComponent } from './components/date-range-picker/date-range-picker.component';
import { PaginationComponent } from './components/pagination/pagination.component';
import { ContactButtonComponent } from './components/contact-button/contact-button.component';
import { ShareIdeaFormComponent } from './components/share-idea-form/share-idea-form.component';
import { ZohoWidgetFormComponent } from './components/zoho-widget-form/zoho-widget-form.component';
import { CanDeactivateManualPayrollGuard } from './guards/can-deactivate-manual-payroll.guard';
import { FeatureFlagGuard } from './guards/feature-flag.guard';
import { AlphaNumericDirective } from './directives/alphanumeric.directive';
import { EmployeeHomeComponent } from './components/employee-home/employee-home.component';
import { EventModalComponent } from './components/event-modal/event-modal.component';
import { TodoTileComponent } from './components/client-home/todo-tile/todo-tile.component';
import { ClientSwitcherComponent } from './components/client-switcher/client-switcher.component';
import { LastPayrollTileComponent } from './components/client-home/last-payroll-tile/last-payroll-tile.component';
import { AdvisorsTileComponent } from './components/client-home/advisors-tile/advisors-tile.component';
import { WebClockComponent } from './components/employee-home/web-clock/web-clock.component';
import { LastPaidComponent } from './components/employee-home/last-paid/last-paid.component';
import { EmployeeMyTimeComponent } from './components/employee-my-time/employee-my-time.component';
import { TimesheetComponent } from './components/employee-my-time/timesheet/timesheet.component';
import { TimeOffComponent } from './components/employee-my-time/time-off/time-off.component';
import { CalendarComponent } from './components/employee-my-time/calendar/calendar.component';
import { SafePipeModule } from 'safe-pipe';
import { ReviewDocumentsComponent } from './components/employee-home/review-documents/review-documents.component';
import { EmployeeMyPayComponent } from './components/employee-my-pay/employee-my-pay.component';
import { PeopleSearchBarComponent } from './components/people-search/people-search-bar.component';
import { PreventMultiClickDirective } from './directives/preventMultiClick.directive';
import { ClientSidePaginationComponent } from './components/client-side-pagination/client-side-pagination.component';
import { EeClientSwitcherComponent } from './components/employee-home/ee-client-switcher/ee-client-switcher.component';
import { EeClientSwitcherWrapperComponent } from './components/employee-home/ee-client-switcher-wrapper/ee-client-switcher-wrapper.component';
import { EmployeeMyFormsComponent } from './components/employee-my-forms/employee-my-forms.component';
import { PleaseWaitComponent } from './components/please-wait/please-wait.component';
import { AbsoluteValuePipe } from './pipes/absolute-value.pipe';
import { ApprovalsTileComponent } from './components/client-home/approvals-tile/approvals-tile.component';
import { AddressChangeReviewModalComponent } from './components/address-change-review-modal/address-change-review-modal.component';
import { DirectDepositChangeReviewModalComponent } from './components/direct-deposit-change-review-modal/direct-deposit-change-review-modal.component';
import { AddressChangeModalComponent } from './components/address-change-modal/address-change-modal.component';
import { DirectDepositDisclaimerModalComponent } from './components/direct-deposit-disclaimer-modal/direct-deposit-disclaimer-modal.component';
import { TimepickerComponent } from './components/timepicker/timepicker.component';
import { ToggleSwitchComponent } from './components/toggle-switch/toggle-switch.component';
import { ConnectedProductsTileComponent } from './components/client-home/connected-products-tile/connected-products-tile.component';
import { ToastsContainerComponent } from './components/toast-container/toast-container.component';
import { TableSorterDirective } from './directives/table-sorter.directive';
import { SelectEditorComponent } from './components/grid/select-editor/select-editor.component';
import { NoCacheHTMLInterceptor } from './interceptors/no-cache-html-interceptor';
import { ClientNavListComponent } from './components/client-nav-list/client-nav-list.component';

@NgModule({
  declarations: [
    AlertModalComponent,
    ArticleComponent,
    ArticleFullPageComponent,
    AuthCallbackComponent,
    AuthIdleComponent,
    AutoCompleteInputComponent,
    AutofocusDirective,
    ClientHomeComponent,
    ClientNotesModalComponent,
    ConfirmModalComponent,
    CPGTagEventDirective,
    LoadingScreenComponent,
    LoggedOutComponent,
    NavHeaderComponent,
    ClientSubNavComponent,
    PageNotFoundComponent,
    ReleaseNotesComponent,
    ClientNavWrapperComponent,
    SearchComponent,
    PassThruWrapperComponent,
    ReportSelectorComponent,
    QuarterPickerComponent,
    QuickReportsComponent,
    DateSuffix,
    CamelCaseToTitleCasePipe,
    EnumToKeyValuePipe,
    FlyInModalComponent,
    MissingPermissionsComponent,
    HasMinRoleDirective,
    NumberInputDirective,
    ConfettiEffectComponent,
    CellRendererComponent,
    CellEditorComponent,
    CustomHeaderIconComponent,
    GridCheckboxComponent,
    MorePayrollActionsPopup,
    CustomGridIconComponent,
    CustomTooltipComponent,
    BaseGridInputComponent,
    HasPermissionDirective,
    ArticleFullPageComponent,
    PayrollWipCSRComponent,
    PayrollWipSupervisorComponent,
    ThirdPartyEmbedComponent,
    MonthPickerComponent,
    GenericPickerComponent,
    CheckboxComponent,
    MultiselectComponent,
    DisabledFieldsetComponent,
    PaginationComponent,
    ContactButtonComponent,
    ShareIdeaFormComponent,
    ZohoWidgetFormComponent,
    UnauthenticatedLandingComponent,
    DateRangePickerComponent,
    AlphaNumericDirective,
    EmployeeHomeComponent,
    EventModalComponent,
    TodoTileComponent,
    LastPayrollTileComponent,
    ClientSwitcherComponent,
    AdvisorsTileComponent,
    WebClockComponent,
    LastPaidComponent,
    EmployeeMyTimeComponent,
    TimesheetComponent,
    TimeOffComponent,
    CalendarComponent,
    ReviewDocumentsComponent,
    EmployeeMyPayComponent,
    PeopleSearchBarComponent,
    PreventMultiClickDirective,
    ClientSidePaginationComponent,
    EeClientSwitcherComponent,
    EeClientSwitcherWrapperComponent,
    EmployeeMyFormsComponent,
    PleaseWaitComponent,
    AbsoluteValuePipe,
    ApprovalsTileComponent,
    AddressChangeReviewModalComponent,
    DirectDepositChangeReviewModalComponent,
    AddressChangeModalComponent,
    DirectDepositDisclaimerModalComponent,
    TimepickerComponent,
    ToggleSwitchComponent,
    ConnectedProductsTileComponent,
    TableSorterDirective,
    SelectEditorComponent,
    ClientNavListComponent
  ],
  exports: [
    AuthCallbackComponent,
    AuthIdleComponent,
    ClientNotesModalComponent,
    ConfirmModalComponent,
    LoadingScreenComponent,
    LoggedOutComponent,
    NavHeaderComponent,
    ClientSubNavComponent,
    PageNotFoundComponent,
    ClientNavWrapperComponent,
    PassThruWrapperComponent,
    SearchComponent,
    DateSuffix,
    CamelCaseToTitleCasePipe,
    CPGTagEventDirective,
    AutofocusDirective,
    EnumToKeyValuePipe,
    AutoCompleteInputComponent,
    AlertModalComponent,
    FlyInModalComponent,
    NgbModule,
    MissingPermissionsComponent,
    HasMinRoleDirective,
    NgxMaskDirective,
    NgxMaskPipe,
    NumberInputDirective,
    ToastsContainerComponent,
    ClientHomeComponent,
    ConfettiEffectComponent,
    CellRendererComponent,
    CellEditorComponent,
    CustomHeaderIconComponent,
    GridCheckboxComponent,
    MorePayrollActionsPopup,
    CustomGridIconComponent,
    CustomTooltipComponent,
    BaseGridInputComponent,
    HasPermissionDirective,
    ArticleFullPageComponent,
    PayrollWipCSRComponent,
    PayrollWipSupervisorComponent,
    ThirdPartyEmbedComponent,
    ReportSelectorComponent,
    QuarterPickerComponent,
    MonthPickerComponent,
    CheckboxComponent,
    MultiselectComponent,
    DisabledFieldsetComponent,
    PaginationComponent,
    ContactButtonComponent,
    DateRangePickerComponent,
    AlphaNumericDirective,
    EmployeeHomeComponent,
    EventModalComponent,
    TodoTileComponent,
    ClientSwitcherComponent,
    WebClockComponent,
    LastPaidComponent,
    ReviewDocumentsComponent,
    PeopleSearchBarComponent,
    PreventMultiClickDirective,
    ClientSidePaginationComponent,
    EmployeeMyPayComponent,
    PleaseWaitComponent,
    AbsoluteValuePipe,
    AddressChangeReviewModalComponent,
    DirectDepositChangeReviewModalComponent,
    AddressChangeModalComponent,
    DirectDepositDisclaimerModalComponent,
    ToggleSwitchComponent,
    TimepickerComponent,
    SelectEditorComponent,
    TableSorterDirective
  ],
  imports: [
    CommonModule,
    FormsModule,
    NgbModule,
    NgIdleKeepaliveModule,
    ReactiveFormsModule,
    RouterModule,
    OAuthModule,
    SafePipeModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ToastsContainerComponent
  ],
  providers: [provideNgxMask(), NumberInputDirective, provideHttpClient(withInterceptorsFromDi())]
})
export class NgSharedLibraryModule {
  public static forRoot(
    environment: any,
    authCodeFlowConfig: any,
    errorInterceptorSkipHeader: any,
    loadingInterceptorSkipHeader: any,
    loginRedirectSkipHeader: any
  ): ModuleWithProviders<NgSharedLibraryModule> {
    return {
      ngModule: NgSharedLibraryModule,
      providers: [
        {
          provide: 'env',
          useValue: environment
        },
        {
          provide: 'authCodeFlowConfig',
          useValue: authCodeFlowConfig
        },
        {
          provide: 'errorInterceptorSkipHeader',
          useValue: errorInterceptorSkipHeader
        },
        {
          provide: 'loadingInterceptorSkipHeader',
          useValue: loadingInterceptorSkipHeader
        },
        {
          provide: 'loginRedirectSkipHeader',
          useValue: loginRedirectSkipHeader
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: HttpErrorInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: LoadingScreenInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: CachingInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: InstanceTypeInterceptor,
          multi: true
        },
        {
          provide: HTTP_INTERCEPTORS,
          useClass: NoCacheHTMLInterceptor,
          multi: true
        },
        {
          provide: NgbDateParserFormatter,
          useClass: NgbDateCustomParserFormatter
        },
        CamelCaseToTitleCasePipe,
        CanDeactivatePersonGuard,
        ClientNotesModalComponent,
        DecimalPipe,
        ModalService,
        NgbActiveModal,
        CanDeactivatePayrollGuard,
        CanDeactivateManualPayrollGuard,
        FeatureFlagGuard,
        SafePipeModule
      ]
    };
  }
}
